import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Copy } from "lucide-react";

interface Props {
  accountId?: string | number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

const IdentityContent: React.FC<Props> = ({ firstName, lastName, email }) => {
  const [feedback, setFeedback] = useState("");
  const [copying, setCopying] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const epoch = useRef(0);
  const emailElement = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const element = emailElement.current;
    const text = element?.firstElementChild as HTMLElement | null;
    if (!element || !text) return;
    let active = true;
    const measure = () => {
      if (!active) return;
      const distance = Math.max(0, text.scrollWidth - element.clientWidth);
      setOverflowing(distance > 0);
      element.style.setProperty("--email-travel", `-${distance}px`);
      element.style.setProperty("--email-duration", `${Math.max(2, distance / 50 + 1)}s`);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(element);
    observer?.observe(text);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [email]);
  useEffect(() => {
    epoch.current += 1;
    return () => {
      epoch.current += 1;
    };
  }, []);
  useEffect(() => {
    if (feedback !== "Email copied.") return;
    const timer = window.setTimeout(() => setFeedback(""), 3000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const copyEmail = async () => {
    if (!email || copying) return;
    const started = epoch.current;
    setCopying(true);
    setFeedback("");
    try {
      if (!navigator.clipboard?.writeText)
        throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(email);
      if (epoch.current === started) setFeedback("Email copied.");
    } catch {
      if (epoch.current === started)
        setFeedback("Select the email address and copy it manually.");
    } finally {
      if (epoch.current === started) setCopying(false);
    }
  };

  return (
    <div className="sidebar-identity-text">
      <h6 className="fw-semibold mb-0 text-dark">
        {firstName} {lastName}
      </h6>
      {email && (
        <div className="sidebar-identity-email-row">
          <small ref={emailElement}
            className={`text-muted sidebar-identity-email${overflowing ? " sidebar-identity-email--overflow" : ""}`}
            tabIndex={overflowing ? 0 : undefined} title={email}>
            <span>{email}</span>
          </small>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary sidebar-email-copy"
            aria-label="Copy email address"
            title="Copy email address"
            disabled={copying}
            onClick={copyEmail}
          >
            <Copy size={12} aria-hidden="true" />
          </button>
        </div>
      )}
      <small className="d-block text-muted" role="status" aria-live="polite">
        {feedback}
      </small>
    </div>
  );
};

// Remount feedback for every authenticated identity or address change.
export const SidebarIdentity: React.FC<Props> = (props) => (
  <IdentityContent
    key={JSON.stringify([props.accountId, props.email])}
    {...props}
  />
);
